import { doc, onSnapshot } from "firebase/firestore";
import { DateTime } from "luxon";
import { z } from "zod";
import { assertNever, classNameOf, reportError } from "../backoffice.utils";
import { auth, firestore } from "../firebase";
import { createApiClient, schemas } from "../generated/serverClient";
import {
  type CombinedExamResult,
  getCombinedExamResult,
  isASFCourseSession,
  isTheoryLesson,
  PracticalExam,
  TheoryExam,
  TheoryLessonAttendanceState,
  TheoryLessonStudentAttendance,
} from "../model/autovioCalendarEvents";
import { CancellationPolicy } from "../model/CancellationPolicy";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { createNote } from "./backoffice.api";
import { theoryLessonConverter } from "../providers/converter/theoryLessonConverter";
import { updateApplied } from "../utils/updateApplied";
import type { DrivingLicenseAuthorityForm } from "../providers/drivingLicenseAuthorityFormsProvider";
import { Invoice } from "../providers/invoicesProvider";
import { DateRange } from "../model/DateRange";
import { autovioCalendarEventConverter } from "../providers/converter/autovioCalendarEventConverter";
import { type Course } from "../providers/coursesProvider";
import { logRequests } from "./logRequests";

export { schemas } from "../generated/serverClient";

export type ServerClient = ReturnType<typeof createApiClient>;

const useLocalBackend = import.meta.env.VITE_USE_LOCAL_BACKEND || false;
const useProdEnv = !!parseInt(import.meta.env.VITE_USE_PRODUCTION_ENVIRONMENT);
export const baseUrl = useProdEnv
  ? "https://server.autovio.de"
  : useLocalBackend
    ? "http://localhost:4242"
    : location.hostname === "backoffice.autovio.de"
      ? "https://server.autovio.de"
      : "https://server.autovio.dev";

export async function getAuthenticatedServerClient(): Promise<ServerClient> {
  const jwt = await auth.currentUser?.getIdToken();
  const serverClient = createApiClient(baseUrl, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${jwt}`,
        // Tell the server, that we would like to get german error messages ...
        "Accept-Language": "de",
      },
    },
  });
  serverClient.use(logRequests());
  if (window.unitTest) {
    serverClient.use({
      name: "intercept all requests",
      request: async (_api, config) => {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw config;
      },
    });
  } else if (window.e2eTest) {
    // Redirect all DELETE, PATCH, POST, and PUT requests to a dummy server address
    // to prevent that any e2e test accidentally creates or changes test data ...
    // see https://www.zodios.org/docs/client/plugins#write-your-own-plugin
    serverClient.use({
      name: "redirect DELETE, PATCH, POST, and PUT requests",
      request: async (_api, config) => {
        const method = config.method?.toLowerCase();
        if (method === "delete" || method.startsWith("p")) {
          (config as Writable<typeof config>).baseURL = "https://pseudo-server.autovio.dev";
        }
        return config;
      },
    });
  }
  return serverClient;
}

export async function setUserDisabled(userId: string, disabled: boolean): Promise<void> {
  const serverClient = await getAuthenticatedServerClient();
  if (disabled) {
    await serverClient.disableUser(undefined, { params: { id: userId } });
  } else {
    await serverClient.enableUser(undefined, { params: { id: userId } });
  }
}

export async function runSafelyWithServerClient<T>(
  action: (serverClient: ServerClient) => Promise<T>,
  options?: RunSafelyOptions<T>,
): Promise<undefined | T> {
  return runSafely(() => getAuthenticatedServerClient().then(action), options);
}

class ServerAPI {
  async getUser(id: string): Promise<UserDto> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getUser({ params: { id } });
    return { id, ...dto };
  }

  /**
   * Side effects:
   * - If the given noticeType is "student_blocked" the status of the student will be changed to "outstandingPayments".
   * - If the given noticeType is "inkasso_notice" a PAIR Finance case file will be created.
   * - A note is added to the student with the content of the email.
   */
  async sendDunningNotice({
    student,
    dunningProcessId,
    noticeType,
    emailSubject,
    emailText,
  }: {
    student: Student;
    dunningProcessId: string;
    noticeType: "student_blocked" | "first_dunning_notice" | "second_dunning_notice" | "inkasso_notice";
    emailSubject: string;
    emailText: string;
  }) {
    const updateReceived = new Promise<void>((resolve) => {
      const oldDunningProcess = JSON.stringify(student.dunningProcess);
      const unsubscribe = studentsProvider.onUpdate(student, (student) => {
        const newDunningProcess = JSON.stringify(student.dunningProcess);
        if (newDunningProcess !== oldDunningProcess) {
          unsubscribe();
          resolve();
        }
      });
    });
    const serverClient = await getAuthenticatedServerClient();
    let localizedNoticeType: string;
    if (noticeType === "student_blocked") {
      const studentUid = student.id;
      await serverClient.blockStudentAndSendPaymentReminder({ studentUid, dunningProcessId, emailSubject, emailText });
      localizedNoticeType = "Zahlungserinnerung";
    } else if (noticeType === "first_dunning_notice") {
      await serverClient.sendFirstDunningNotice({ dunningProcessId, emailSubject, emailText });
      localizedNoticeType = "1. Mahnung";
    } else if (noticeType === "second_dunning_notice") {
      await serverClient.sendSecondDunningNotice({ dunningProcessId, emailSubject, emailText });
      localizedNoticeType = "2. Mahnung";
    } else if (noticeType === "inkasso_notice") {
      await serverClient.handoverToPairFinance({ dunningProcessId, emailSubject, emailText });
      localizedNoticeType = "Inkasso";
    } else {
      assertNever(noticeType);
    }
    try {
      await createNote({
        studentUid: student.id,
        body: `<p style="font-weight: bold; color: red">E-Mail: ${localizedNoticeType}</p>
<p>Wir haben an ${student.firstName} folgende Nachricht geschickt:</p>
<p>${emailText.replaceAll("\n", "<br />\n")}</p>`,
      });
    } catch (error) {
      reportError(`Failed to create note "Mahnwesen - ${localizedNoticeType} ..."`, error);
    }
    await updateReceived;
    await queryClient.invalidateQueries();
  }

  /**
   * Side effect:
   * - A note is added to the student with the content of the email.
   */
  async sendSammelquittungEmail({
    student,
    emailRecipient,
    emailSubject,
    emailText,
    emailHtml,
    invoices,
  }: {
    student: Student;
    emailRecipient: string;
    emailSubject: string;
    emailText: string;
    emailHtml: string;
    invoices: Array<Invoice>;
  }) {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.sendSammelquittungEmail({
      recipient: emailRecipient,
      subject: emailSubject,
      text: emailText,
      html: emailHtml,
      invoices: invoices.map((it) => it.downloadUrl!),
    });
    // Prevent that the note is created by the dunning.e2e-test.ts ...
    if (!window.e2eTest) {
      try {
        await createNote({
          studentUid: student.id,
          body: `<p style="font-weight: bold; color: red">Mahnwesen - Sammelquittung</p>
  <p>Wir haben an ${emailRecipient} folgende Nachricht geschickt:</p>
  <pre>${emailText}</pre>`,
        });
      } catch (error) {
        reportError(`Failed to create note "Mahnwesen - Sammelquittung ..."`, error);
      }
    }
    await queryClient.invalidateQueries();
  }

  async cancelBookedTraining({
    studentId,
    bookedTrainingId,
    cancelReason,
  }: {
    studentId: string;
    bookedTrainingId: string;
    cancelReason?: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.cancelBookedTraining(
      { cancelReason: cancelReason ?? null },
      { params: { id: studentId, bookedTrainingId } },
    );
  }

  async reactivateCancelledBookedTraining({
    studentId,
    bookedTrainingId,
  }: {
    studentId: string;
    bookedTrainingId: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.reactivateBookedTraining(undefined, { params: { id: studentId, bookedTrainingId } });
  }

  async addStudentToCourse({
    student,
    course,
    agreedCancellationPolicy,
  }: {
    student: Student;
    course: Course;
    agreedCancellationPolicy?: CancellationPolicy;
  }) {
    if (course.isBKFCourse) {
      throw new Error("Noch nicht implementiert.");
    }
    const lastEvent = course.events.at(-1)!;
    const studentWasAdded = new Promise<void>((resolve) => {
      const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${lastEvent.id}`), (snapshot) => {
        const studentUids = snapshot.data()?.derived?.studentUids;
        if (Array.isArray(studentUids) && studentUids.includes(student.id)) {
          unsubscribe();
          resolve();
        }
      });
    });
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.addStudentToCourse({ studentUid: student.id, courseUid: course.id, agreedCancellationPolicy });
    await studentWasAdded;
    void queryClient.invalidateQueries();
  }

  async removeStudentFromCourse({ student, course }: { student: Student; course: Course }) {
    if (course.isBKFCourse) {
      throw new Error("Noch nicht implementiert.");
    }
    const watchedEvent = course.events.findLast((it) =>
      isTheoryLesson(it)
        ? it.students[student.id]?.rsvp === "accepted"
        : isASFCourseSession(it)
          ? it.participants[student.id]
          : false,
    );
    if (!watchedEvent) {
      throw new Error(`Could not find an event in course ${course.id} which is booked by ${student.id}`);
    }
    const studentWasRemoved = new Promise<void>((resolve) => {
      const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${watchedEvent.id}`), (snapshot) => {
        const studentUids = snapshot.data()?.derived?.studentUids;
        if (Array.isArray(studentUids) && !studentUids.includes(student.id)) {
          unsubscribe();
          resolve();
        }
      });
    });
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.removeStudentFromCourse({ studentUid: student.id, courseUid: course.id });
    await studentWasRemoved;
    void queryClient.invalidateQueries();
  }

  _getExpectedTheoryLessonAttendance = (
    attendance: "attended" | "no-show" | "cancelled" | "booked" | boolean,
  ): Partial<TheoryLessonStudentAttendance> => {
    switch (attendance) {
      case false:
      case "no-show":
        return {
          rsvp: "accepted",
          attended: false,
          noShow: true,
        };
      case true:
      case "attended":
        return {
          rsvp: "accepted",
          attended: true,
          noShow: false,
        };
      case "booked":
        return {
          rsvp: "accepted",
        };
      case "cancelled":
        return {
          rsvp: "rejected",
        };
      default:
        return {};
    }
  };

  async updateTheoryLessonAttendance({
    eventId,
    attendance,
  }: {
    eventId: string;
    attendance: Record<string, TheoryLessonAttendanceState | boolean>;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    const payload = schemas.UpdateAttendanceDto.parse({ attendance });

    const expectedStudents = Object.entries(attendance).reduce(
      (acc, [studentId, studentAttendance]) => ({
        ...acc,
        [studentId]: this._getExpectedTheoryLessonAttendance(studentAttendance),
      }),
      {} satisfies Record<string, Partial<TheoryLessonStudentAttendance>>,
    );

    const updateReceived = new Promise<void>((resolve) => {
      const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${eventId}`), (snapshot) => {
        const updatedTheoryLesson = theoryLessonConverter.fromFirestore(snapshot);
        if (updateApplied({ students: expectedStudents }, updatedTheoryLesson)) {
          unsubscribe();
          resolve();
        }
      });
    });
    await serverClient.updateAttendance(payload, { params: { eventId } });
    await updateReceived;
  }

  async editCalendarEvent({ eventId, dto }: { eventId: string; dto: EditCalendarEventDto }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    const doc_ = doc(firestore, `calendar_events/${eventId}`);
    const updateReceived = new Promise<void>((resolve) => {
      if (window.e2eTest) {
        resolve();
        return;
      }
      const unsubscribe = onSnapshot(doc_, (snapshot) => {
        const updatedCalendarEvent = autovioCalendarEventConverter.fromFirestore(snapshot);
        if (
          updateApplied(
            {
              ...dto,
              ...(dto.start ? { start: DateTime.fromISO(dto.start) } : {}),
              ...(dto.end ? { end: DateTime.fromISO(dto.end) } : {}),
            },
            updatedCalendarEvent,
          )
        ) {
          unsubscribe();
          resolve();
        }
      });
    });
    console.info(`Editing calendar event ${eventId} ...`, dto);
    await serverClient.editEvent_v3(dto, { params: { eventId } });
    await updateReceived;
  }

  async createCalendarEvent(dto: CreateCalendarEventDto): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    const doc_ = doc(firestore, `calendar_events/${dto.id ?? dto.uid}`);
    console.info(`Creating calendar event ...`, dto);
    await serverClient.createCalendarEvent(dto);
    await new Promise<void>((resolve, reject) => {
      if (window.e2eTest) {
        resolve();
        return;
      }
      const unsubscribe = onSnapshot(
        doc_,
        (snapshot) => {
          if (snapshot.exists()) {
            try {
              // Can the calendar event be deserialized? ...
              autovioCalendarEventConverter.fromFirestore(snapshot);
              unsubscribe();
              resolve();
            } catch (error) {
              console.info(`Failed to deserialize ${doc_.path}`, error);
            }
          }
        },
        reject,
      );
    });
  }

  async getBranch({ id }: { id: string }): Promise<BranchDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getBranch({ params: { id } });
  }

  async getBranches({ drivingSchoolId }: { drivingSchoolId: string }): Promise<BranchDto[]> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.listBranches({ queries: { drivingSchoolId } });
  }

  async createBranch({ payload }: { payload: CreateBranchDto }): Promise<BranchDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.createBranch(payload);
  }

  async updateBranch({ id, payload }: { id: string; payload: UpdateBranchDto }): Promise<BranchDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.updateBranch(payload, { params: { id } });
  }

  async getDrivingLicenseAuthorityForm({
    authorityId,
    id,
  }: {
    authorityId: string;
    id: string;
  }): Promise<DrivingLicenseAuthorityForm> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getDrivingLicenseAuthorityForm({ params: { authorityId, id } });
  }

  async createDrivingLicenseAuthorityForm({
    authorityId,
    dto,
  }: {
    authorityId: string;
    dto: CreateDrivingLicenseAuthorityFormDto;
  }): Promise<DrivingLicenseAuthorityForm> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.createDrivingLicenseAuthorityForm(dto, { params: { authorityId: authorityId } });
  }

  async attachPdf({
    authorityId,
    formId,
    pdf,
  }: {
    authorityId: string;
    formId: string;
    pdf: File;
  }): Promise<DrivingLicenseAuthorityForm> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.attachPdfToForm({ pdf }, { params: { authorityId, formId } });
  }

  async updateDrivingLicenseAuthorityForm({
    authorityId,
    formId,
    dto,
  }: {
    authorityId: string;
    formId: string;
    dto: UpdateDrivingLicenseAuthorityFormDto;
  }): Promise<DrivingLicenseAuthorityForm> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.updateDrivingLicenseAuthorityForm(dto, { params: { authorityId, formId } });
  }

  async deleteDrivingLicenseAuthorityForm({
    authorityId,
    formId,
  }: {
    authorityId: string;
    formId: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.deleteDrivingLicenseAuthorityForm(undefined, { params: { authorityId, formId } });
  }

  async createDrivingLicenseAuthorityInstruction({
    authorityId,
    dto,
  }: {
    authorityId: string;
    dto: CreateDrivingLicenseAuthorityInstructionDto;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.createDrivingLicenseAuthorityInstruction(dto, { params: { id: authorityId } });
  }

  async updateDrivingLicenseAuthorityInstruction({
    authorityId,
    instructionId,
    dto,
  }: {
    authorityId: string;
    instructionId: string;
    dto: UpdateDrivingLicenseAuthorityInstructionDto;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.updateDrivingLicenseAuthorityInstruction(dto, { params: { authorityId, instructionId } });
  }

  async assignBranchToDrivingLicenseAuthority({
    branchId,
    authorityId,
  }: {
    branchId: string;
    authorityId: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.assignBranchToDrivingLicenseAuthority(undefined, { params: { authorityId, branchId } });
  }

  async unassignBranchFromDrivingLicenseAuthority({
    branchId,
    authorityId,
  }: {
    branchId: string;
    authorityId: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.unassignBranchFromDrivingLicenseAuthority(undefined, { params: { authorityId, branchId } });
  }

  async getEmployee(id: string): Promise<EmployeeDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getEmployee({ params: { id } });
  }

  async getDrivingSchoolEmployees(drivingSchoolId: string): Promise<Array<EmployeeDto>> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getDrivingSchoolEmployees({ params: { id: drivingSchoolId } });
  }

  async updateAuthEmail({ studentId, email }: { studentId: string; email: string }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    const { data: student } = await studentsProvider.getOne("students", { id: studentId });
    const updateReceived = new Promise<void>((resolve) => {
      const unsubscribe = studentsProvider.onUpdate(student, (student) => {
        if (student.email === email) {
          unsubscribe();
          resolve();
        }
      });
    });
    await serverClient.updateAuthEmail({ email }, { params: { id: studentId } });
    await updateReceived;
  }

  async deleteStudentAttachment({
    studentId,
    attachmentId,
  }: {
    studentId: string;
    attachmentId: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteStudentAttachment(undefined, {
      params: { id: studentId, attachmentId },
    });
  }

  async deleteStudentDocument({
    studentId,
    documentName,
    drivingSchoolId,
  }: {
    studentId: string;
    documentName: string;
    drivingSchoolId: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteStudentDocument(undefined, {
      params: { id: studentId, documentName },
      queries: { drivingSchoolId },
    });
  }

  async deleteSignatureRequest({ requestId }: { requestId: string }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteSignatureRequest(undefined, { params: { requestId } });
  }

  async getExamLocation({ id, drivingSchoolId }: { id: string; drivingSchoolId: string }): Promise<ExamLocationDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getExamLocation({ params: { id, drivingSchoolId } });
  }

  async getExamLocations({ drivingSchoolId }: { drivingSchoolId: string }): Promise<ExamLocationDto[]> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.listExamLocations({ params: { drivingSchoolId } });
  }

  async createExamLocation({
    dto,
    drivingSchoolId,
  }: {
    dto: CreateExamLocationDto;
    drivingSchoolId: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.createExamLocation(dto, { params: { drivingSchoolId } });
  }

  async updateExamLocation({
    id,
    dto,
    drivingSchoolId,
  }: {
    id: string;
    dto: UpdateExamLocationDto;
    drivingSchoolId: string;
  }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.updateExamLocation(dto, { params: { id, drivingSchoolId } });
  }

  async deleteExamLocation({ id, drivingSchoolId }: { id: string; drivingSchoolId: string }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteExamLocation(undefined, { params: { id, drivingSchoolId } });
  }

  async getFilledOutAuthorityFormPdf(
    studentId: string,
    formId: string,
    quoteId: string,
    withoutBackground: boolean,
  ): Promise<ArrayBuffer> {
    const serverClient = await getAuthenticatedServerClient();
    return (await serverClient.fillOutForm({
      params: { id: studentId, formId },
      queries: { withoutBackground, quoteId },
      responseType: "arraybuffer",
    })) as unknown as ArrayBuffer;
  }

  async getInstructorWorktimeStats(instructorId: string, dateRange: DateRange): Promise<WorktimeStatsDto2> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getInstructorWorktimeStats_v2({
      params: { id: instructorId },
      queries: { from: dateRange[0].toString(), to: dateRange[1].toString() },
    });
  }

  async getDrivingSchoolLabel({
    id,
    drivingSchoolId,
  }: {
    id: string;
    drivingSchoolId: string;
  }): Promise<DrivingSchoolLabelDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getDrivingSchoolLabel({ params: { id, drivingSchoolId } });
  }

  async getDrivingSchoolLabels({ drivingSchoolId }: { drivingSchoolId: string }): Promise<ListDrivingSchoolLabelDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.listDrivingSchoolLabels({ params: { drivingSchoolId } });
  }

  async createDrivingSchoolLabel({
    drivingSchoolId,
    payload,
  }: {
    drivingSchoolId: string;
    payload: CreateDrivingSchoolLabelDto;
  }): Promise<DrivingSchoolLabelDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.createDrivingSchoolLabel(payload, { params: { drivingSchoolId } });
  }

  async updateDrivingSchoolLabel({
    id,
    drivingSchoolId,
    payload,
  }: {
    id: string;
    drivingSchoolId: string;
    payload: UpdateDrivingSchoolLabelDto;
  }): Promise<DrivingSchoolLabelDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.updateDrivingSchoolLabel(payload, { params: { id, drivingSchoolId } });
  }

  async deleteDrivingSchoolLabel({ id, drivingSchoolId }: { id: string; drivingSchoolId: string }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteDrivingSchoolLabel(undefined, { params: { id, drivingSchoolId } });
  }

  async getOtherEventCategory({
    id,
    drivingSchoolId,
  }: {
    id: string;
    drivingSchoolId: string;
  }): Promise<OtherEventCategoryDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getOtherEventCategory({ params: { id, drivingSchoolId } });
  }

  async getOtherEventCategories({ drivingSchoolId }: { drivingSchoolId: string }): Promise<ListOtherEventCategoryDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.listOtherEventCategories({ params: { drivingSchoolId } });
  }

  async createOtherEventCategory({
    drivingSchoolId,
    payload,
  }: {
    drivingSchoolId: string;
    payload: CreateOtherEventCategoryDto;
  }): Promise<OtherEventCategoryDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.createOtherEventCategory(payload, { params: { drivingSchoolId } });
  }

  async updateOtherEventCategory({
    id,
    drivingSchoolId,
    payload,
  }: {
    id: string;
    drivingSchoolId: string;
    payload: UpdateOtherEventCategoryDto;
  }): Promise<OtherEventCategoryDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.updateOtherEventCategory(payload, { params: { id, drivingSchoolId } });
  }

  async deleteOtherEventCategory({ id, drivingSchoolId }: { id: string; drivingSchoolId: string }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteOtherEventCategory(undefined, { params: { id, drivingSchoolId } });
  }

  async downloadStudentMonthlyAbsenceReport(studentId: string, year: string, month: string): Promise<ArrayBuffer> {
    const serverClient = await getAuthenticatedServerClient();
    return (await serverClient.createSGBIIIBescheinigungFehlzeiten({
      params: { studentId, year, month },
      responseType: "arraybuffer",
    })) as unknown as ArrayBuffer;
  }

  async getRoom({ id, drivingSchoolId }: { id: string; drivingSchoolId: string }): Promise<RoomDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getRoom({ params: { id, drivingSchoolId } });
  }

  async getRooms({ drivingSchoolId }: { drivingSchoolId: string }): Promise<RoomDto[]> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getRooms({ params: { drivingSchoolId } });
  }

  async createRoom({
    drivingSchoolId,
    payload,
  }: {
    drivingSchoolId: string;
    payload: CreateRoomDto;
  }): Promise<RoomDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.createRoom(payload, { params: { drivingSchoolId } });
  }

  async updateRoom({
    id,
    drivingSchoolId,
    payload,
  }: {
    id: string;
    drivingSchoolId: string;
    payload: UpdateRoomDto;
  }): Promise<RoomDto> {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.updateRoom(payload, { params: { id, drivingSchoolId } });
  }

  async deleteRoom({ id, drivingSchoolId }: { id: string; drivingSchoolId: string }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteRoom(undefined, { params: { id, drivingSchoolId } });
  }

  async getStudentsSignupsStats({
    drivingSchoolId,
    from,
    to,
    groupBy,
    branchId,
  }: {
    drivingSchoolId: string;
    from: string;
    to: string;
    groupBy: "day" | "month" | "drivingLicenseClass";
    branchId?: string;
  }): Promise<StudentsSignupsStatsDto> {
    const serverClient = await getAuthenticatedServerClient();
    const response = await serverClient.getStudentsSignupsStats({
      params: { drivingSchoolId },
      queries: { from, to, groupBy, ...(branchId ? { branchId } : {}) },
    });
    return response;
  }

  async listSubscriptionPackages(drivingSchoolId: string) {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.listSubscriptionPackages({ queries: { drivingSchoolId } });
  }

  async getOverviewStats(drivingSchoolId: string) {
    const serverClient = await getAuthenticatedServerClient();
    return serverClient.getOverviewStats({ params: { drivingSchoolId } });
  }

  async updateExamResult({ eventId, result }: { eventId: string; result: CombinedExamResult }): Promise<void> {
    const serverClient = await getAuthenticatedServerClient();
    const updateReceived = new Promise<void>((resolve) => {
      const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${eventId}`), (snapshot) => {
        const event = autovioCalendarEventConverter.fromFirestore(snapshot);
        if (getCombinedExamResult(event as TheoryExam | PracticalExam) === result) {
          unsubscribe();
          resolve();
        }
      });
    });
    await serverClient.updateExamResult({ result }, { params: { eventId } });
    await updateReceived;
  }
}

export const serverAPI = new ServerAPI();

const DateTimeToStringSchema = z
  .any()
  .transform((val) =>
    typeof val === "string" ? DateTime.fromISO(val) : val instanceof Date ? DateTime.fromJSDate(val) : val,
  )
  .refine(
    (val) => DateTime.isDateTime(val),
    (val) => ({ message: "string, Date, or DateTime expected, got: " + classNameOf(val) }),
  )
  .refine(
    (val) => val.isValid,
    (val) => ({ message: val.invalidExplanation ?? "invalid DateTime" }),
  )
  .transform((val) => val.toISO()) as any as ReturnType<typeof z.string>;

export const DateTimeFromStringSchema = z
  .string()
  .transform((val) => DateTime.fromISO(val, { zone: "Europe/Berlin" }))
  .refine(
    (val) => val.isValid,
    (val) => ({ message: val.invalidExplanation ?? "invalid DateTime string" }),
  );

export type BranchDto = z.infer<typeof schemas.BranchDto>;
export type CreateBranchDto = z.infer<typeof schemas.CreateBranchDto>;
export type UpdateBranchDto = z.infer<typeof schemas.UpdateBranchDto>;
export type CreateCalendarEventDto = z.infer<typeof schemas.CreateCalendarEventDto>;
export type EditCalendarEventDto = z.infer<typeof schemas.EditCalendarEventDto3>;
export type ExamLocationDto = z.infer<typeof schemas.ExamLocationDto>;
export type CreateExamLocationDto = z.infer<typeof schemas.CreateExamLocationDto>;
export type UpdateExamLocationDto = z.infer<typeof schemas.UpdateExamLocationDto>;
export type UserDto = z.infer<typeof schemas.UserDto> & { id: string };
export type CreateDrivingLicenseAuthorityFormDto = z.infer<typeof schemas.CreateDrivingLicenseAuthorityFormDto>;
export type UpdateDrivingLicenseAuthorityFormDto = z.infer<typeof schemas.UpdateDrivingLicenseAuthorityFormDto>;
export type CreateDrivingLicenseAuthorityInstructionDto = z.infer<
  typeof schemas.CreateDrivingLicenseAuthorityInstructionDto
>;
export type UpdateDrivingLicenseAuthorityInstructionDto = z.infer<
  typeof schemas.UpdateDrivingLicenseAuthorityInstructionDto
>;
export type StudentSearchResultDto = z.infer<typeof schemas.StudentSearchResultDto>;
export type ListDrivingSchoolLabelDto = z.infer<typeof schemas.ListDrivingSchoolLabelDto>;
export type DrivingSchoolLabelDto = z.infer<typeof schemas.DrivingSchoolLabelDto>;
export type CreateDrivingSchoolLabelDto = z.infer<typeof schemas.CreateDrivingSchoolLabelDto>;
export type UpdateDrivingSchoolLabelDto = z.infer<typeof schemas.UpdateDrivingSchoolLabelDto>;
export type OtherEventCategoryDto = z.infer<typeof schemas.OtherEventCategoryDto>;
export type ListOtherEventCategoryDto = z.infer<typeof schemas.ListOtherEventCategoryDto>;
export type CreateOtherEventCategoryDto = z.infer<typeof schemas.CreateOtherEventCategoryDto>;
export type UpdateOtherEventCategoryDto = z.infer<typeof schemas.UpdateOtherEventCategoryDto>;
export type InstructorSearchResultDto = z.infer<typeof schemas.InstructorSearchResultDto>;
export type DrivingSchoolSearchResultDto = z.infer<typeof schemas.DrivingSchoolSearchResultDto>;
export type GuardianSearchResultDto = z.infer<typeof schemas.GuardianSearchResultDto>;
export type StudentsSignupsStatsDto = z.infer<typeof schemas.StudentSignupStatsDto>;
export type SubscriptionPackageDto = z.infer<typeof schemas.SubscriptionPackageDto>;
export const CreateInstructorDtoSchema = schemas.CreateInstructorDto.omit({ startDate: true, endDate: true }).extend({
  startDate: DateTimeToStringSchema,
  endDate: DateTimeToStringSchema.optional(),
});
export const CreateEmployeeDtoSchema = schemas.CreateEmployeeDto;
export type EmployeeDto = z.infer<typeof schemas.EmployeeDto>;
export type WorktimeStatsDto2 = z.infer<typeof schemas.WorktimeStatsDto2>;
export type RoomDto = z.infer<typeof schemas.RoomDto>;
export type CreateRoomDto = z.infer<typeof schemas.CreateRoomDto>;
export type UpdateRoomDto = z.infer<typeof schemas.UpdateRoomDto>;
// Get a clean zod schema for ExamAuthorityFeeDto
// (without the `[key: string]: unknown` from the .passthrough() ...
const _ExamAuthorityFeeDtoSchema = z.object(schemas.ExamAuthorityFeeDto.shape);
export type ExamAuthorityFeeDto = z.infer<typeof _ExamAuthorityFeeDtoSchema>;
export type VogelFahrschulmanagerContext = z.infer<typeof schemas.VogelFahrschulmanagerContext>;
